import axios from "api/axios";

export interface BulkAssignError {
  employee_id: number;
  detail: string;
}

export interface BulkAssignResult {
  assigned: unknown[];
  errors: BulkAssignError[];
}

export type ContactStatus =
  | "new"
  | "engaging"
  | "connection_sent"
  | "connected"
  | "messaged"
  | "responded"
  | "converted"
  | "not_relevant"
  | "archived";

export const STATUS_OPTIONS: { value: ContactStatus; label: string }[] = [
  { value: "new",             label: "New" },
  { value: "engaging",        label: "Engaging" },
  { value: "connection_sent", label: "Connection Sent" },
  { value: "connected",       label: "Connected" },
  { value: "messaged",        label: "Messaged" },
  { value: "responded",       label: "Responded" },
  { value: "converted",       label: "Converted" },
  { value: "not_relevant",    label: "Not Relevant" },
  { value: "archived",        label: "Archived" },
];

// Company engagement pipeline — you can't connect to or message a LinkedIn
// company page, only follow it and engage with its posts. "converted" means a
// hiring contact was identified there (tracked separately as a HiringContact).
export type CompanyStatus = "new" | "following" | "engaging" | "converted" | "not_relevant" | "archived";

export const COMPANY_STATUS_OPTIONS: { value: CompanyStatus; label: string }[] = [
  { value: "new",          label: "New" },
  { value: "following",    label: "Following" },
  { value: "engaging",     label: "Engaging" },
  { value: "converted",    label: "Converted" },
  { value: "not_relevant", label: "Not Relevant" },
  { value: "archived",     label: "Archived" },
];

export interface Company {
  id: number;
  name: string;
  domain: string;
  linkedin_url?: string | null;
  industry?: string | null;
  employee_count_range?: string | null;
  is_recruiting_agency?: boolean | null;
  funding_stage?: string | null;
  total_funding_usd?: number | null;
  annual_revenue_usd?: number | null;
  num_jobs?: number | null;
  num_jobs_last_30_days?: number | null;
  tech_tags: string[];
  source: string;
  is_watched: boolean;
  priority?: "high" | "medium" | "low" | null;
  is_active: boolean;
  archived_at?: string | null;
  created_at?: string;
  updated_at?: string;

  // Present when loaded from a /company-assignments endpoint
  assignment_id?: number;
  status?: CompanyStatus;
  assignment_notes?: string | null;
  assignment_from_status?: string | null;
  assignment_created_at?: string | null;
  assignment_updated_at?: string | null;
  employee_id?: number; // admin view only

  // Everyone (if anyone) currently working this company — a company can be
  // assigned to multiple TMs at once, each with their own independent status.
  // Present on items from /company-assignments endpoints.
  assigned_employees?: { assignment_id: number; employee_id: number; name: string; status: CompanyStatus }[];
}

export interface HiringContact {
  id: number;
  company_id: number;
  full_name: string;
  role?: string | null;
  linkedin_url: string;
  thumbnail_url?: string | null;
  tech_tags: string[];
  source: string;
  is_watched: boolean;
  priority?: "high" | "medium" | "low" | null;
  is_active: boolean;
  archived_at?: string | null;
  created_at?: string;
  updated_at?: string;

  // Present when loaded from an /assignments endpoint
  company?: Company;
  assignment_id?: number;
  assigned_by?: string;
  status?: ContactStatus;
  assignment_notes?: string | null;
  assignment_from_status?: string | null;
  assignment_created_at?: string | null;
  assignment_updated_at?: string | null;
  employee_id?: number; // admin view only

  // Everyone (if anyone) currently working this contact — a contact can be assigned
  // to multiple TMs at once, each with their own independent status/notes/history.
  // Present on items from /contacts and /assignments endpoints alike.
  assigned_employees?: { assignment_id: number; employee_id: number; name: string; status: ContactStatus }[];
}

export interface Paged<T> {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: T[];
}

export interface AssignableEmployee {
  id: number;
  employee_code: string;
  name: string;
  email: string;
}

export interface HistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export interface CompanyFilters {
  page?: number;
  page_size?: number;
  search?: string;
  industry?: string;
  is_recruiting_agency?: boolean;
  is_watched?: boolean;
  assigned_only?: boolean;
  is_active?: boolean;
}

export interface ContactFilters {
  page?: number;
  page_size?: number;
  company_id?: number;
  assigned_only?: boolean;
  search?: string;
  is_watched?: boolean;
  is_active?: boolean;
}

export interface AssignmentFilters {
  page?: number;
  page_size?: number;
  employee_id?: number;
  status?: string;
  hide_archived?: boolean;
  company_id?: number;
  search?: string;
  is_watched?: boolean;
}

export interface CompanyAssignee {
  assignment_id: number;
  employee_id: number;
  employee_code: string;
  name: string;
  status: CompanyStatus;
}

export interface CompanyAssignmentFilters {
  page?: number;
  page_size?: number;
  employee_id?: number;
  status?: string;
  hide_archived?: boolean;
  search?: string;
  is_watched?: boolean;
}

export interface ContactAssignee {
  assignment_id: number;
  employee_id: number;
  employee_code: string;
  name: string;
  status: ContactStatus;
}

function cleanParams(filters: object) {
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));
}

export async function fetchAssignableEmployees(): Promise<AssignableEmployee[]> {
  const res = await axios.get("/admin/hiring-contacts/employees");
  return res.data;
}

export async function fetchCompanies(filters: CompanyFilters = {}, isAdmin = true): Promise<Paged<Company>> {
  const base = isAdmin ? "/admin/hiring-contacts/companies" : "/employee/hiring-contacts/companies";
  const res = await axios.get(base, { params: cleanParams(filters) });
  return res.data;
}

export async function fetchContacts(filters: ContactFilters = {}): Promise<Paged<HiringContact>> {
  const res = await axios.get("/admin/hiring-contacts/contacts", { params: cleanParams(filters) });
  return res.data;
}

export async function assignContact(contactId: number, employeeIds: number[]): Promise<BulkAssignResult> {
  const res = await axios.post(`/admin/hiring-contacts/contacts/${contactId}/assign`, { employee_ids: employeeIds });
  return res.data;
}

export async function unassignContact(assignmentId: number): Promise<void> {
  await axios.delete(`/admin/hiring-contacts/assignments/${assignmentId}`);
}

export async function fetchAssignmentStatusCounts(isAdmin: boolean, employeeId?: number, isWatched?: boolean): Promise<Record<string, number>> {
  const base = isAdmin ? "/admin/hiring-contacts/assignments/status-counts" : "/employee/hiring-contacts/assignments/status-counts";
  const params = cleanParams({
    employee_id: isAdmin && employeeId ? employeeId : undefined,
    is_watched: isWatched,
  });
  const res = await axios.get(base, { params });
  return res.data;
}

export async function fetchAssignments(filters: AssignmentFilters = {}, isAdmin = false): Promise<Paged<HiringContact>> {
  const base = isAdmin ? "/admin/hiring-contacts/assignments" : "/employee/hiring-contacts/assignments";
  const res = await axios.get(base, { params: cleanParams(filters) });
  return res.data;
}

// Fetches a single assignment by id — used when a specific TM's status chip is
// clicked but that assignment's row isn't on the currently loaded page (pagination
// is by assignment row, so a contact's other TM assignments can land elsewhere).
export async function fetchAssignmentDetail(assignmentId: number, isAdmin: boolean): Promise<HiringContact> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const res = await axios.get(`${base}/assignments/${assignmentId}`);
  return res.data;
}

export async function updateAssignmentStatus(
  assignmentId: number,
  status: ContactStatus,
  // Omit entirely (undefined) to leave existing notes untouched — the backend treats a
  // present "notes" key, even null, as an explicit request to clear it.
  notes: string | null | undefined,
  isAdmin: boolean,
): Promise<void> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const body: { status: ContactStatus; notes?: string | null } = { status };
  if (notes !== undefined) body.notes = notes;
  await axios.patch(`${base}/assignments/${assignmentId}/status`, body);
}

export async function fetchAssignmentHistory(assignmentId: number, isAdmin: boolean): Promise<HistoryEntry[]> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const res = await axios.get(`${base}/assignments/${assignmentId}/history`);
  return res.data;
}

export async function setCompanyWatch(companyId: number, isWatched: boolean): Promise<Company> {
  const res = await axios.patch(`/admin/hiring-contacts/companies/${companyId}/watch`, { is_watched: isWatched });
  return res.data;
}

export async function setContactWatch(contactId: number, isWatched: boolean): Promise<HiringContact> {
  const res = await axios.patch(`/admin/hiring-contacts/contacts/${contactId}/watch`, { is_watched: isWatched });
  return res.data;
}

export async function setCompanyPriority(companyId: number, priority: "high" | "medium" | "low" | null): Promise<Company> {
  const res = await axios.patch(`/admin/hiring-contacts/companies/${companyId}/priority`, { priority });
  return res.data;
}

export async function setContactPriority(contactId: number, priority: "high" | "medium" | "low" | null): Promise<HiringContact> {
  const res = await axios.patch(`/admin/hiring-contacts/contacts/${contactId}/priority`, { priority });
  return res.data;
}

// Archive / restore / delete — admin only. Archiving a company cascades to its
// contacts server-side; restoring does not cascade-restore them.

export async function archiveCompany(companyId: number): Promise<Company> {
  const res = await axios.patch(`/admin/hiring-contacts/companies/${companyId}/archive`);
  return res.data;
}

export async function restoreCompany(companyId: number): Promise<Company> {
  const res = await axios.patch(`/admin/hiring-contacts/companies/${companyId}/restore`);
  return res.data;
}

export async function deleteCompany(companyId: number): Promise<void> {
  await axios.delete(`/admin/hiring-contacts/companies/${companyId}`);
}

export async function archiveContact(contactId: number): Promise<HiringContact> {
  const res = await axios.patch(`/admin/hiring-contacts/contacts/${contactId}/archive`);
  return res.data;
}

export async function restoreContact(contactId: number): Promise<HiringContact> {
  const res = await axios.patch(`/admin/hiring-contacts/contacts/${contactId}/restore`);
  return res.data;
}

export async function deleteContact(contactId: number): Promise<void> {
  await axios.delete(`/admin/hiring-contacts/contacts/${contactId}`);
}

export interface CleanupPreview {
  count: number;
  older_than_days: number;
}

export interface CleanupResult {
  deleted: number;
  older_than_days: number;
}

export async function fetchArchivedCompaniesPreview(olderThanDays: number): Promise<CleanupPreview> {
  const res = await axios.get("/admin/hiring-contacts/companies/cleanup/archived-preview", {
    params: { older_than_days: olderThanDays },
  });
  return res.data;
}

export async function deleteArchivedCompanies(olderThanDays: number): Promise<CleanupResult> {
  const res = await axios.delete("/admin/hiring-contacts/companies/cleanup/archived", {
    params: { older_than_days: olderThanDays },
  });
  return res.data;
}

export async function fetchArchivedContactsPreview(olderThanDays: number): Promise<CleanupPreview> {
  const res = await axios.get("/admin/hiring-contacts/contacts/cleanup/archived-preview", {
    params: { older_than_days: olderThanDays },
  });
  return res.data;
}

export async function deleteArchivedContacts(olderThanDays: number): Promise<CleanupResult> {
  const res = await axios.delete("/admin/hiring-contacts/contacts/cleanup/archived", {
    params: { older_than_days: olderThanDays },
  });
  return res.data;
}

export async function createCompany(name: string, domain: string, linkedinUrl: string, isAdmin = true): Promise<Company> {
  const base = isAdmin ? "/admin/hiring-contacts/companies" : "/employee/hiring-contacts/companies";
  const res = await axios.post(base, { name, domain, linkedin_url: linkedinUrl });
  return res.data;
}

export async function createContact(fullName: string, linkedinUrl: string, companyId: number, isAdmin = true): Promise<HiringContact> {
  const base = isAdmin ? "/admin/hiring-contacts/contacts" : "/employee/hiring-contacts/contacts";
  const res = await axios.post(base, {
    full_name: fullName, linkedin_url: linkedinUrl, company_id: companyId,
  });
  return res.data;
}

export async function assignCompany(companyId: number, employeeIds: number[]): Promise<BulkAssignResult> {
  const res = await axios.post(`/admin/hiring-contacts/companies/${companyId}/assign`, { employee_ids: employeeIds });
  return res.data;
}

export async function unassignCompany(assignmentId: number): Promise<void> {
  await axios.delete(`/admin/hiring-contacts/company-assignments/${assignmentId}`);
}

export async function fetchCompanyAssignees(companyId: number): Promise<CompanyAssignee[]> {
  const res = await axios.get(`/admin/hiring-contacts/companies/${companyId}/assignees`);
  return res.data;
}

export async function fetchCompanyAssignmentStatusCounts(isAdmin: boolean, employeeId?: number, isWatched?: boolean): Promise<Record<string, number>> {
  const base = isAdmin ? "/admin/hiring-contacts/company-assignments/status-counts" : "/employee/hiring-contacts/company-assignments/status-counts";
  const params = cleanParams({
    employee_id: isAdmin && employeeId ? employeeId : undefined,
    is_watched: isWatched,
  });
  const res = await axios.get(base, { params });
  return res.data;
}

// Admin only — the "Assigned" scope badge in the merged Companies tab needs a
// distinct-company count (not a sum of per-status assignment rows, which
// double-counts a company assigned to more than one TM) so it stays consistent
// with the "Unassigned" scope's distinct-company count.
export async function fetchCompanyAssignedDistinctCount(isWatched?: boolean): Promise<number> {
  const res = await axios.get("/admin/hiring-contacts/company-assignments/assigned-count", {
    params: cleanParams({ is_watched: isWatched }),
  });
  return res.data.count;
}

export async function fetchCompanyAssignments(filters: CompanyAssignmentFilters = {}, isAdmin = false): Promise<Paged<Company>> {
  const base = isAdmin ? "/admin/hiring-contacts/company-assignments" : "/employee/hiring-contacts/company-assignments";
  const res = await axios.get(base, { params: cleanParams(filters) });
  return res.data;
}

// Fetches a single company assignment by id — used when a specific TM's status
// chip is clicked but that assignment's row isn't on the currently loaded page.
export async function fetchCompanyAssignmentDetail(assignmentId: number, isAdmin: boolean): Promise<Company> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const res = await axios.get(`${base}/company-assignments/${assignmentId}`);
  return res.data;
}

export async function updateCompanyAssignmentStatus(
  assignmentId: number,
  status: CompanyStatus,
  // Omit entirely (undefined) to leave existing notes untouched — the backend treats a
  // present "notes" key, even null, as an explicit request to clear it.
  notes: string | null | undefined,
  isAdmin: boolean,
): Promise<void> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const body: { status: CompanyStatus; notes?: string | null } = { status };
  if (notes !== undefined) body.notes = notes;
  await axios.patch(`${base}/company-assignments/${assignmentId}/status`, body);
}

export async function fetchCompanyAssignmentHistory(assignmentId: number, isAdmin: boolean): Promise<HistoryEntry[]> {
  const base = isAdmin ? "/admin/hiring-contacts" : "/employee/hiring-contacts";
  const res = await axios.get(`${base}/company-assignments/${assignmentId}/history`);
  return res.data;
}

export async function fetchContactAssignees(contactId: number): Promise<ContactAssignee[]> {
  const res = await axios.get(`/admin/hiring-contacts/contacts/${contactId}/assignees`);
  return res.data;
}
